import {
  type BasicCheckMerchantsResponse,
  type NowMerchantExtended,
  NowSlotExtendedTypeEnum,
  type NowSlotExtendedV2,
} from '@redteclab/api/clients/bully'

import { NOW_NOT_ELIGIBLE_REASON_MINIMUM_VALUE_NOT_REACHED } from './NOW_NOT_ELIGIBLE_REASON'

/**
 * used if to show now details for check on specific product.
 * it may be that the product is not eligible due to no minimum value reached.
 * in this case we anyway want to show the now details for the product.
 */
export const nowBasicCheckRespIsEligibleOrMinimumValueNotReached = (
  response?: BasicCheckMerchantsResponse,
): boolean => {
  if (!response) {
    return false
  }

  if (response.nowEligible) {
    return true
  }

  return (
    response.notEligibleReason ===
    NOW_NOT_ELIGIBLE_REASON_MINIMUM_VALUE_NOT_REACHED
  )
}

/**
 * slot.orderEndDateTime is given in YYYY-MM-DD HH:mm:ss format which can't be easily parsed.
 * function accepts string in format "YYYY-MM-DD HH:mm:ss" transforms to "YYYY-MM-DDTHH:mm:ss.000000+01:00"
 * and returns Date object.
 */
export const nowBasicCheckRespGetSlotOrderEndDateTimeInIsoBerlinTime = (
  slot: NowSlotExtendedV2,
): Date | undefined => {
  const endTimeString = slot.orderEndDateTime
  if (!endTimeString) {
    return undefined
  }

  try {
    const [datePart, timePart] = endTimeString.split(' ')
    const [year, month, day] = datePart.split('-')
    const [hours, minutes, seconds] = timePart.split(':')

    if (!year || !month || !day || !hours || !minutes || !seconds) {
      return undefined
    }

    const date = new Date(
      `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.000000`,
    )
    // check if valid date
    if (Number.isNaN(date.getTime())) {
      return undefined
    }

    return date
  } catch {
    return undefined
  }
}

/**
 * Return first slot default shipping cost, in local currency, not in the lowest-value coin
 */
export const nowBasicCheckRespGetSlotShippingCostAmountDefault = (
  slots?: NowSlotExtendedV2[],
): number | undefined =>
  slots?.[0]?.shippingCostsAmountDefault
    ? slots[0]?.shippingCostsAmountDefault / 100
    : undefined

export const nowBasicCheckRespGetSlotShippingIsToday = (
  slot: NowSlotExtendedV2,
): boolean => {
  return slot.type === NowSlotExtendedTypeEnum.SameDay
}

/**
 * Return first slot minimum cost to get free shipping, in local currency, not in the lowest-value coin
 */
export const nowBasicCheckRespFirstSlotNoShippingCostsIfTotalMinimum = (
  slots?: NowSlotExtendedV2[],
): number =>
  slots?.length && slots[0]?.noShippingCostsIfTotalMinimumCents
    ? slots[0]?.noShippingCostsIfTotalMinimumCents / 100
    : 0

/**
 * Return Merchant minimum order value, in local currency, not in the lowest-value coin
 */
export const nowBasicCheckRespMerchantMinimumOrderValue = (
  merchant?: NowMerchantExtended,
): number | undefined =>
  merchant?.minimumOrderValue ? merchant.minimumOrderValue / 100 : undefined

export const nowBasicCheckRespMerchantHasAddressInfo = (
  merchant?: NowMerchantExtended,
): boolean =>
  Boolean(
    merchant?.merchantName &&
      merchant.street &&
      merchant.zipCode &&
      !Number.isNaN(merchant.streetNo) &&
      merchant.city,
  )

const nowBasicCheckRespGetSlotsGroupedByType = (
  response: BasicCheckMerchantsResponse,
): { nextDaySlots: NowSlotExtendedV2[]; sameDaySlots: NowSlotExtendedV2[] } => {
  const { slots } = response
  const sameDaySlots: NowSlotExtendedV2[] = []
  const nextDaySlots: NowSlotExtendedV2[] = []

  if (slots) {
    for (const slot of slots) {
      if (slot.type === NowSlotExtendedTypeEnum.SameDay) {
        sameDaySlots.push(slot)
      }
      if (slot.type === NowSlotExtendedTypeEnum.NextDay) {
        nextDaySlots.push(slot)
      }
    }
  }

  return { nextDaySlots, sameDaySlots }
}

/** used for sorting **/
const compareSlotsBySecondsLeft = (
  aSlot: NowSlotExtendedV2,
  bSlot: NowSlotExtendedV2,
): number => {
  return (aSlot.secondsLeft ?? 0) - (bSlot.secondsLeft ?? 0)
}

/**
 * returns slots grouped by type and sorted by seconds left.
 */
const nowBasicCheckRespGetSlotGroupsSortedBySecondsLeft = (
  response: BasicCheckMerchantsResponse,
): { nextDaySlots: NowSlotExtendedV2[]; sameDaySlots: NowSlotExtendedV2[] } => {
  const { nextDaySlots, sameDaySlots } =
    nowBasicCheckRespGetSlotsGroupedByType(response)

  sameDaySlots.sort(compareSlotsBySecondsLeft)
  nextDaySlots.sort(compareSlotsBySecondsLeft)

  return { nextDaySlots, sameDaySlots }
}

/**
 * returns the slot which will be used for "time left countdown" https://jira.shop-apotheke.com/browse/WSTRA-5351
 */
export const nowBasicCheckRespGetSlotForCountdown = (
  response: BasicCheckMerchantsResponse,
): NowSlotExtendedV2 | undefined => {
  const { nextDaySlots, sameDaySlots } =
    nowBasicCheckRespGetSlotGroupsSortedBySecondsLeft(response)

  if (sameDaySlots.length > 0) {
    return sameDaySlots.at(-1)
  }

  if (nextDaySlots.length > 0) {
    return nextDaySlots.at(-1)
  }

  return undefined
}

export const nowBasicCheckGetSlotsByType = (
  response: BasicCheckMerchantsResponse,
  type: NowSlotExtendedTypeEnum,
): NowSlotExtendedV2[] => {
  const { nextDaySlots, sameDaySlots } =
    nowBasicCheckRespGetSlotsGroupedByType(response)

  if (type === NowSlotExtendedTypeEnum.SameDay) {
    return sameDaySlots
  }

  return nextDaySlots
}

export const nowBasicCheckRespHasNowAvailabilityInSelectedArea = (
  response: BasicCheckMerchantsResponse | undefined,
): boolean =>
  Boolean(response?.zipCodeInDeliveryRegion) ||
  Boolean(response?.zipCodeInPickupRegion)
